import {costumeUpload, spriteUpload} from './file-uploader.js';

const cleanImageBase64 = imageBase64 => imageBase64.replace(
    /^data:image\/[a-zA-Z0-9.+-]+;base64,/,
    ''
);

const loadBase64Image = imageBase64 => new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = error => reject(error);

    image.src = `data:image/png;base64,${cleanImageBase64(imageBase64)}`;
});

export const prepareAIBackdrop = async (imageBase64, storage) => {
    const image = await loadBase64Image(imageBase64);

    const canvas = document.createElement('canvas');
    canvas.width = 480;
    canvas.height = 360;

    const context = canvas.getContext('2d');

    const sourceAspect = image.width / image.height;
    const targetAspect = 480 / 360;

    let sourceX = 0;
    let sourceY = 0;
    let sourceWidth = image.width;
    let sourceHeight = image.height;

    if (sourceAspect > targetAspect) {
        sourceWidth = image.height * targetAspect;
        sourceX = (image.width - sourceWidth) / 2;
    } else if (sourceAspect < targetAspect) {
        sourceHeight = image.width / targetAspect;
        sourceY = (image.height - sourceHeight) / 2;
    }

    context.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        480,
        360
    );

    const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(result => {
            if (!result) {
                reject(new Error('Failed to create AI backdrop PNG.'));
                return;
            }

            resolve(result);
        }, 'image/png');
    });

    const buffer = await blob.arrayBuffer();

    return new Promise((resolve, reject) => {
        costumeUpload(
            buffer,
            'image/png',
            storage,
            vmCostumes => {
                vmCostumes.forEach((costume, index) => {
                    costume.name =
                        `AI Backdrop${index ? ` ${index + 1}` : ''}`;
                });

                resolve(vmCostumes);
            },
            reject
        );
    });
};

export const prepareAISprite = async (imageBase64, storage, spriteName = 'AI Sprite') => {
    const image = await loadBase64Image(imageBase64);

    const sourceCanvas = document.createElement('canvas');
    sourceCanvas.width = image.width;
    sourceCanvas.height = image.height;

    const sourceContext = sourceCanvas.getContext('2d');

    sourceContext.clearRect(
        0,
        0,
        sourceCanvas.width,
        sourceCanvas.height
    );

    sourceContext.drawImage(image, 0, 0);

    const imageData = sourceContext.getImageData(
        0,
        0,
        sourceCanvas.width,
        sourceCanvas.height
    );

    const {data, width, height} = imageData;

    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const alpha = data[((y * width) + x) * 4 + 3];

            if (alpha > 10) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
            }
        }
    }

    if (maxX < minX || maxY < minY) {
        throw new Error('Generated sprite contains no visible pixels.');
    }

    const cropWidth = (maxX - minX) + 1;
    const cropHeight = (maxY - minY) + 1;

    const maxSpriteDimension = 110;
    const padding = 6;

    const scale = Math.min(
        1,
        maxSpriteDimension / Math.max(cropWidth, cropHeight)
    );

    const spriteWidth = Math.max(
        1,
        Math.round(cropWidth * scale)
    );

    const spriteHeight = Math.max(
        1,
        Math.round(cropHeight * scale)
    );

    const outputCanvas = document.createElement('canvas');

    outputCanvas.width = spriteWidth + (padding * 2);
    outputCanvas.height = spriteHeight + (padding * 2);

    const outputContext = outputCanvas.getContext('2d');

    outputContext.clearRect(
        0,
        0,
        outputCanvas.width,
        outputCanvas.height
    );

    outputContext.drawImage(
        sourceCanvas,
        minX,
        minY,
        cropWidth,
        cropHeight,
        padding,
        padding,
        spriteWidth,
        spriteHeight
    );

    const blob = await new Promise((resolve, reject) => {
        outputCanvas.toBlob(result => {
            if (!result) {
                reject(new Error('Failed to create AI sprite PNG.'));
                return;
            }

            resolve(result);
        }, 'image/png');
    });

    const buffer = await blob.arrayBuffer();

    return new Promise((resolve, reject) => {
        spriteUpload(
            buffer,
            'image/png',
            `${spriteName}.png`,
            storage,
            newSprite => resolve(newSprite),
            reject
        );
    });
};